import { type FC, useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { WizardStepper, ProgressMessage } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { CountdownTimer, ErrorBoundary } from '@/components/shared';
import { ShoppingCart } from 'lucide-react';
import { useCountdown, useEventRegistrationSettings } from '@/hooks';
import { supabase } from '@/lib/api/supabase';
import type {
  RegistrationWizardState,
  Cart,
  CartItem,
  DbEventRegistration,
  Tournament,
  Addon,
  EventRegistration
} from '@/types';
import { dbToEventRegistration } from '@/types';
import { deriveEntryState, buildEntryCartItems, tournamentSelectionsForEntry } from '../utils/registrationEntryState';
import {
  EventEntryStep,
  TournamentSelectionStep,
  AddonsSelectionStep,
  CheckoutStep,
  ConfirmationStep,
  CancelConfirmDialog,
} from '../components';
import { saveCart, loadCart, clearCart } from '@/lib/utils/cartPersistence';
import { getActivePrice } from '@/lib/utils/stripe';

const WIZARD_STEPS = [
  { id: 1, name: 'Event Entry', description: 'Choose entry type' },
  { id: 2, name: 'Tournaments', description: 'Select competitions' },
  { id: 3, name: 'Add-ons', description: 'Choose merchandise' },
  { id: 4, name: 'Checkout', description: 'Review and pay' },
  { id: 5, name: 'Confirmation', description: 'Complete!' },
];

const TAX_RATE = 0.083; // 8.3% tax rate for Arizona

/**
 * Main registration wizard page
 * Orchestrates the multi-step registration flow
 */
export const RegistrationWizardPage: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedTournamentId = (location.state as { preselectedTournamentId?: string })?.preselectedTournamentId;
  const initialStep = searchParams.get('step') ? parseInt(searchParams.get('step')!, 10) : null;
  const { registrationBlocked, targetDate, isLoading: countdownLoading } = useCountdown();
  const { settings: eventRegSettings, isLoading: eventRegSettingsLoading } = useEventRegistrationSettings();

  const [wizardState, setWizardState] = useState<RegistrationWizardState>({
    currentStep: 1,
    completedSteps: [],
    cart: {
      items: [],
      subtotal: 0,
      tax: 0,
      total: 0,
    },
    eventRegistration: { wantsToPay: false },
    tournaments: { selectedIds: preselectedTournamentId ? [preselectedTournamentId] : [] },
    addons: { selections: [] },
  });

  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [existingRegistration, setExistingRegistration] = useState<EventRegistration | null>(null);
  // Ids of day passes the user already holds (completed rows with day_pass_id set)
  const [ownedDayPassIds, setOwnedDayPassIds] = useState<string[]>([]);
  const [existingTournamentIds, setExistingTournamentIds] = useState<string[]>([]);
  const [existingAddonPurchases, setExistingAddonPurchases] = useState<Array<{ addonId: string; quantity: number; variantData?: Record<string, unknown> }>>([]);
  const [loading, setLoading] = useState(true);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [isFreeRegistration, setIsFreeRegistration] = useState(false);
  const [freeRegistrationError, setFreeRegistrationError] = useState<string | null>(null);
  const [showProgressMessage, setShowProgressMessage] = useState(false);
  const [progressMessage, setProgressMessage] = useState('Processing...');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  // Ref for focus management
  const mainContentRef = useRef<HTMLElement>(null);

  // Check if user already registered for the event and fetch data
  useEffect(() => {
    const initializeWizard = async (): Promise<void> => {
      try {
        setLoading(true);

        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError || !user) {
          // Not logged in - redirect to login
          window.location.href = '/login?redirect=/register';
          return;
        }

        // Fetch tournaments, addons, and existing registration in parallel
        const [tournamentsResult, addonsResult, regResult] = await Promise.all([
          supabase
            .from('tournaments')
            .select('*')
            .eq('status', 'open')
            .order('date', { ascending: true }),
          supabase
            .from('addons')
            .select('*')
            .eq('is_active', true)
            .order('sort_order', { ascending: true }),
          supabase
            .from('event_registrations')
            .select('*')
            .eq('user_id', user.id)
            .eq('event_year', 2026)
            .eq('payment_status', 'completed'),
        ]);

        // Process tournaments
        if (tournamentsResult.error) {
          console.error('Failed to fetch tournaments:', tournamentsResult.error);
        } else if (tournamentsResult.data) {
          // Convert database tournaments to Tournament type
          setTournaments(tournamentsResult.data.map(t => ({
            id: t.id,
            name: t.name,
            slug: t.slug,
            weapon: t.weapon,
            division: t.division,
            description: t.description || '',
            date: t.date,
            startTime: t.start_time,
            endTime: t.end_time || undefined,
            maxParticipants: t.max_participants,
            currentParticipants: t.current_participants || 0,
            waitlistHeldSpots: (t as any).waitlist_held_spots || 0,
            registrationFee: t.registration_fee,
            earlyBirdPrice: (t as any).early_bird_price || null,
            earlyBirdStartDate: (t as any).early_bird_start_date || null,
            earlyBirdEndDate: (t as any).early_bird_end_date || null,
            stripeEarlyBirdPriceId: (t as any).stripe_early_bird_price_id || null,
            location: t.location,
            status: t.status,
            rules: t.rules || undefined,
            headerImageUrl: t.header_image_url || null,
            galleryImages: t.gallery_images || [],
            visible: t.visible,
            displayOrder: (t as any).display_order ?? 0,
            stripeProductId: t.stripe_product_id || undefined,
            stripePriceId: t.stripe_price_id || undefined,
            createdAt: t.created_at,
            updatedAt: t.updated_at,
          })));
        }

        // Process add-ons
        if (addonsResult.error) {
          console.error('Failed to fetch add-ons:', addonsResult.error);
        } else if (addonsResult.data) {
          // Convert database addons to Addon type
          setAddons(addonsResult.data.map(a => ({
            id: a.id,
            name: a.name,
            slug: a.slug,
            description: a.description || '',
            category: a.category,
            price: a.price,
            hasInventory: a.has_inventory,
            stockQuantity: a.stock_quantity || undefined,
            reservedQuantity: a.reserved_quantity || 0,
            maxPerOrder: a.max_per_order || undefined,
            hasVariants: a.has_variants,
            variants: a.variants || undefined,
            stripeProductId: a.stripe_product_id || undefined,
            stripePriceId: a.stripe_price_id || undefined,
            imageUrl: a.image_url || undefined,
            galleryUrls: a.gallery_urls || undefined,
            availableFrom: a.available_from || null,
            availableUntil: a.available_until || null,
            isActive: a.is_active,
            sortOrder: a.sort_order,
            featured: a.featured || false,
            createdAt: a.created_at,
            updatedAt: a.updated_at,
          })));
        }

        // Check for existing registrations: at most one weekend row, any
        // number of day passes. Day passes do NOT lock the entry step.
        let weekendRegRow: DbEventRegistration | null = null;
        if (regResult.error) {
          console.error('Failed to check registration:', regResult.error);
        } else if (regResult.data) {
          const entryState = deriveEntryState(regResult.data as DbEventRegistration[]);
          weekendRegRow = entryState.weekendRegistration;
          if (weekendRegRow) {
            setExistingRegistration(dbToEventRegistration(weekendRegRow));
          }
          setOwnedDayPassIds(entryState.ownedDayPassIds);
        }

        // Always fetch existing tournament registrations (regardless of event registration)
        const { data: tournamentRegs, error: tournamentRegsError } = await supabase
          .from('tournament_registrations')
          .select('tournament_id')
          .eq('user_id', user.id)
          .eq('payment_status', 'completed');

        let tIds: string[] = [];
        if (!tournamentRegsError && tournamentRegs) {
          tIds = tournamentRegs.map(tr => tr.tournament_id);
          setExistingTournamentIds(tIds);
          // Pre-select existing tournaments
          setWizardState((prev) => ({
            ...prev,
            tournaments: {
              selectedIds: [...tIds, ...(preselectedTournamentId && !tIds.includes(preselectedTournamentId) ? [preselectedTournamentId] : [])],
            },
          }));
        }

        // Fetch existing add-on purchases from order_items via orders table
        // First get the user's completed orders, then get addon items from those orders
        const { data: userOrders, error: ordersError } = await supabase
          .from('orders')
          .select('id')
          .eq('user_id', user.id)
          .eq('payment_status', 'completed');

        if (!ordersError && userOrders && userOrders.length > 0) {
          const orderIds = userOrders.map(o => o.id);

          const { data: orderItems, error: orderItemsError } = await supabase
            .from('order_items')
            .select('addon_id, quantity, variant_data')
            .in('order_id', orderIds)
            .eq('item_type', 'addon')
            .not('addon_id', 'is', null);

          if (!orderItemsError && orderItems) {
            // Aggregate purchases - same addon+variant should sum quantities
            const purchaseMap = new Map<string, { addonId: string; quantity: number; variantData?: Record<string, unknown> }>();

            orderItems.forEach(item => {
              // Create a unique key based on addon_id and variant_data
              const variantKey = item.variant_data ? JSON.stringify(item.variant_data) : '';
              const key = `${item.addon_id}::${variantKey}`;

              const existing = purchaseMap.get(key);
              if (existing) {
                existing.quantity += item.quantity ?? 0;
              } else {
                const purchase: { addonId: string; quantity: number; variantData?: Record<string, unknown> } = {
                  addonId: item.addon_id!,
                  quantity: item.quantity ?? 0,
                };
                if (item.variant_data) {
                  purchase.variantData = item.variant_data as Record<string, unknown>;
                }
                purchaseMap.set(key, purchase);
              }
            });

            setExistingAddonPurchases(Array.from(purchaseMap.values()));
          }
        }

        // Try to restore persisted cart if available. Day-pass-only holders may
        // resume a cart and re-enter step 1; only a weekend registration or
        // existing tournament entries lock the entry step.
        const persistedCart = loadCart(user.id);
        const hasWeekendReg = weekendRegRow !== null;
        if (persistedCart && !hasWeekendReg && tIds.length === 0) {
          console.log('Restoring persisted cart state');
          const restored = persistedCart.wizardState;
          // Normalize carts persisted before multi-day-pass support
          if (restored.eventRegistration.dayPassId && !restored.eventRegistration.dayPassIds) {
            restored.eventRegistration = {
              ...restored.eventRegistration,
              dayPassIds: [restored.eventRegistration.dayPassId],
            };
          }
          setWizardState(restored);
        } else if (hasWeekendReg || tIds.length > 0) {
          // Skip to tournament selection if already registered for the event
          // (weekend tier) OR has existing tournament registrations
          const targetStep = initialStep && initialStep >= 2 && initialStep <= 3 ? initialStep : 2;
          const completedStepsArray = Array.from({ length: targetStep - 1 }, (_, i) => i + 1);
          setWizardState((prev) => ({
            ...prev,
            currentStep: targetStep,
            completedSteps: completedStepsArray,
            eventRegistration: { wantsToPay: false },
          }));
        }
      } catch (err) {
        console.error('Failed to initialize wizard:', err);
      } finally {
        setLoading(false);
      }
    };

    void initializeWizard();
  }, [initialStep]);

  // Save cart to localStorage whenever wizard state changes
  useEffect(() => {
    const saveCartState = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (user && wizardState.currentStep < 5) {
          // Don't save if we're on confirmation step
          saveCart(user.id, wizardState.cart, wizardState);
        }
      } catch (error) {
        console.error('Failed to save cart:', error);
      }
    };

    // Only save if not loading (to avoid saving initial empty state)
    if (!loading) {
      void saveCartState();
    }
  }, [wizardState, loading]);

  // Create stable keys for cart-relevant wizard state to avoid unnecessary recalculations
  const eventRegistrationKey = useMemo(
    () => JSON.stringify(wizardState.eventRegistration),
    [wizardState.eventRegistration]
  );
  const selectedTournamentIds = useMemo(
    () => JSON.stringify(wizardState.tournaments.selectedIds),
    [wizardState.tournaments.selectedIds]
  );
  const addonSelections = useMemo(
    () => JSON.stringify(wizardState.addons.selections),
    [wizardState.addons.selections]
  );

  // Recalculate cart whenever selections change
  useEffect(() => {
    const calculateCart = (): Cart => {
      // Event registration entries: one weekend item, or one item per selected
      // day pass (already-owned days are skipped defensively)
      const items: CartItem[] = buildEntryCartItems({
        eventRegistration: wizardState.eventRegistration,
        settings: eventRegSettings,
        hasWeekendRegistration: !!existingRegistration,
        ownedDayPassIds,
      });

      // Add tournaments (only NEW ones, not already purchased). Defensively
      // filter via tournamentSelectionsForEntry so stale persisted carts for
      // day-pass users cannot include tournament items.
      tournamentSelectionsForEntry(wizardState.eventRegistration.entryType, wizardState.tournaments.selectedIds).forEach((tournamentId) => {
        // Skip tournaments that user has already purchased
        if (existingTournamentIds.includes(tournamentId)) {
          return;
        }
        const tournament = tournaments.find((t) => t.id === tournamentId);
        if (tournament) {
          const { price: tournamentPrice, isEarlyBird } = getActivePrice(tournament);
          items.push({
            id: `temp-tournament-${tournamentId}`,
            type: 'tournament' as const,
            itemId: tournament.id,
            name: `${tournament.name}${isEarlyBird ? ' (Early Bird)' : ''}`,
            description: `${tournament.weapon} - ${tournament.division}`,
            quantity: 1,
            price: tournamentPrice,
          });
        }
      });

      // Add addons
      wizardState.addons.selections.forEach((selection) => {
        const addon = addons.find((a) => a.id === selection.addonId);
        if (addon) {
          items.push({
            id: `temp-addon-${selection.addonId}-${Date.now()}`,
            type: 'addon' as const,
            itemId: addon.id,
            name: addon.name,
            description: addon.description,
            quantity: selection.quantity,
            price: addon.price,
            ...(selection.variantData && {
              variant: {
                name: 'Custom',
                data: selection.variantData,
              },
            }),
          });
        }
      });

      const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const tax = Math.round(subtotal * TAX_RATE);
      const total = subtotal + tax;

      return { items, subtotal, tax, total };
    };

    setWizardState((prev) => ({
      ...prev,
      cart: calculateCart(),
    }));
  }, [
    eventRegistrationKey,
    selectedTournamentIds,
    addonSelections,
    tournaments,
    addons,
    existingRegistration,
    existingTournamentIds,
    eventRegSettings,
    ownedDayPassIds,
  ]);

  // Handle free registration - create event registration via server-side Edge Function
  const handleFreeRegistration = async (): Promise<string | null> => {
    try {
      setFreeRegistrationError(null);
      setProgressMessage('Creating your free registration...');
      setShowProgressMessage(true);

      const { data, error: fnError } = await supabase.functions.invoke('create-free-registration', {
        body: {
          eventYear: 2026,
          entryName: `Cactus Cup 2026 - ${eventRegSettings.freeEntryName}`,
          entryDescription: eventRegSettings.freeEntryDescription,
        },
      });

      if (fnError) {
        throw new Error(fnError.message || 'Failed to complete registration');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (data?.alreadyRegistered) {
        // User was already registered — redirect to dashboard
        navigate('/dashboard');
        return null;
      }

      return data?.orderId ?? null;
    } catch (err) {
      console.error('Free registration error:', err);
      setFreeRegistrationError(err instanceof Error ? err.message : 'Failed to complete registration');
      return null;
    } finally {
      setShowProgressMessage(false);
    }
  };

  const handleStepComplete = async (step: number, data: unknown): Promise<void> => {
    // Ensure no duplicate step IDs in completedSteps to prevent progress bar exceeding 100%
    const updatedCompletedSteps = wizardState.completedSteps.includes(step)
      ? wizardState.completedSteps
      : [...wizardState.completedSteps, step];

    const newState = {
      ...wizardState,
      completedSteps: updatedCompletedSteps,
      currentStep: step + 1,
      ...(step === 1 && { eventRegistration: data as RegistrationWizardState['eventRegistration'] }),
      ...(step === 2 && { tournaments: data as RegistrationWizardState['tournaments'] }),
      ...(step === 3 && { addons: data as RegistrationWizardState['addons'] }),
    };
    
    // Focus management: move focus to main content after step change
    setTimeout(() => {
      if (mainContentRef.current) {
        mainContentRef.current.focus();
      }
    }, 100);

    // Handle free entry flow
    if (step === 1) {
      const eventData = data as RegistrationWizardState['eventRegistration'];
      const isPaid = eventData.entryType === 'paid';
      const isSpectator = eventData.entryType === 'spectator';
      const isDayPass = eventData.entryType === 'day_pass';
      // Spectator and day-pass tiers share the same step-visibility settings
      const isAttendanceTier = isSpectator || isDayPass;

      // Determine which settings to use based on entry type.
      // Paid entry sees every step; attendance/free tiers honor their visibility flags.
      const tierShowTournaments = isPaid
        ? true
        : isDayPass
          ? false // day passes are attendance-only: no tournament registration
          : isSpectator
            ? eventRegSettings.showTournamentsForSpectator
            : eventRegSettings.showTournamentsForFree;
      const showAddons = isPaid
        ? true
        : isAttendanceTier
          ? eventRegSettings.showAddonsForSpectator
          : eventRegSettings.showAddonsForFree;

      // Global override: when new tournament registrations are closed, the
      // tournament step is skipped entirely for new registrants.
      const showTournaments = eventRegSettings.tournamentRegistrationEnabled && tierShowTournaments;

      // Day passes are attendance-only: drop any preselected tournaments so a
      // tournament-page CTA can't ride into a day-pass checkout unseen.
      if (isDayPass) {
        newState.tournaments = { selectedIds: tournamentSelectionsForEntry('day_pass', newState.tournaments.selectedIds) };
      }

      // If user chose free entry and neither tournaments nor addons are shown for free
      if (!eventData.wantsToPay && !showTournaments && !showAddons) {
        // Skip directly to confirmation - process free registration
        setLoading(true);
        const registrationOrderId = await handleFreeRegistration();
        setLoading(false);

        if (registrationOrderId) {
          setIsFreeRegistration(true);
          setOrderId(registrationOrderId);
          setWizardState({
            ...newState,
            completedSteps: [1, 2, 3, 4],
            currentStep: 5,
          });
          // Clear persisted cart on successful free registration
          clearCart();
          return;
        }
      }

      // If a paying tier (paid/spectator/day pass) has nothing else to add, skip to checkout
      if (eventData.wantsToPay && !showTournaments && !showAddons) {
        setWizardState({
          ...newState,
          completedSteps: [1, 2, 3],
          currentStep: 4,
        });
        return;
      }

      // If tournaments are not available, skip the tournament step and go to addons (step 3)
      if (!showTournaments) {
        setWizardState({
          ...newState,
          completedSteps: [1, 2],
          currentStep: 3,
        });
        return;
      }
    }

    // If step 2 (tournaments) completed with free/spectator entry and addons not allowed
    if (step === 2) {
      const isAttendanceTier = wizardState.eventRegistration.entryType === 'spectator'
        || wizardState.eventRegistration.entryType === 'day_pass';
      const showAddons = isAttendanceTier
        ? eventRegSettings.showAddonsForSpectator
        : eventRegSettings.showAddonsForFree;

      if ((!wizardState.eventRegistration.wantsToPay || isAttendanceTier) && !showAddons) {
        const tournamentData = data as RegistrationWizardState['tournaments'];
        // Check if any NEW tournaments were selected (not already purchased)
        const newTournaments = tournamentData.selectedIds.filter(id => !existingTournamentIds.includes(id));

        if (newTournaments.length === 0 && !wizardState.eventRegistration.wantsToPay) {
          // No new items and free entry, skip to confirmation with free registration
          setLoading(true);
          const registrationOrderId = await handleFreeRegistration();
          setLoading(false);

          if (registrationOrderId) {
            setIsFreeRegistration(true);
            setOrderId(registrationOrderId);
            setWizardState({
              ...newState,
              completedSteps: [1, 2, 3, 4],
              currentStep: 5,
            });
            // Clear persisted cart on successful free registration
            clearCart();
            return;
          }
        } else {
          // Has items to pay for (tournaments or spectator pass), skip addons and go to checkout
          setWizardState({
            ...newState,
            completedSteps: [1, 2, 3],
            currentStep: 4,
          });
          return;
        }
      }
    }

    // After step 3 (addons), check if we can skip checkout
    if (step === 3) {
      // Calculate what would be in the cart
      const tournamentData = wizardState.tournaments;
      const addonData = data as RegistrationWizardState['addons'];

      const newTournaments = tournamentData.selectedIds.filter(id => !existingTournamentIds.includes(id));
      const hasNewTournaments = newTournaments.length > 0;
      const hasAddons = addonData.selections.length > 0;
      const wantsToPay = wizardState.eventRegistration.wantsToPay;

      // If nothing to pay for (free entry + no new tournaments + no addons)
      if (!wantsToPay && !hasNewTournaments && !hasAddons) {
        // Skip checkout, go directly to confirmation
        setLoading(true);
        const registrationOrderId = await handleFreeRegistration();
        setLoading(false);

        if (registrationOrderId) {
          setIsFreeRegistration(true);
          setOrderId(registrationOrderId);
          setWizardState({
            ...newState,
            addons: addonData,
            completedSteps: [1, 2, 3, 4],
            currentStep: 5,
          });
          // Clear persisted cart on successful free registration
          clearCart();
          return;
        }
      }
    }

    // Default: just move to next step
    setWizardState(newState);
  };

  const handleStepBack = (): void => {
    setWizardState((prev) => {
      const isAttendanceTier = prev.eventRegistration.entryType === 'spectator'
        || prev.eventRegistration.entryType === 'day_pass';
      const isFreeOrAttendance = !prev.eventRegistration.wantsToPay || isAttendanceTier;

      // Determine which steps should be shown based on entry type
      const isDayPassTier = prev.eventRegistration.entryType === 'day_pass';
      const tierShowTournaments = isFreeOrAttendance
        ? (isDayPassTier
            ? false
            : isAttendanceTier
              ? eventRegSettings.showTournamentsForSpectator
              : eventRegSettings.showTournamentsForFree)
        : true;
      // Tournament step is also hidden when new tournament registrations are closed
      const showTournaments = eventRegSettings.tournamentRegistrationEnabled && tierShowTournaments;
      const showAddons = isFreeOrAttendance
        ? (isAttendanceTier ? eventRegSettings.showAddonsForSpectator : eventRegSettings.showAddonsForFree)
        : true;

      let targetStep = prev.currentStep - 1;

      // Skip over locked steps when going back
      if (isFreeOrAttendance) {
        // Going back from step 4 (checkout) - check if addons should be skipped
        if (prev.currentStep === 4 && !showAddons) {
          targetStep = showTournaments ? 2 : 1;
        }
        // Going back from step 3 (addons) - check if tournaments should be skipped
        else if (prev.currentStep === 3 && !showTournaments) {
          targetStep = 1;
        }
      }

      return {
        ...prev,
        currentStep: Math.max(1, targetStep),
      };
    });

    // Focus management: move focus to main content after step change
    setTimeout(() => {
      if (mainContentRef.current) {
        mainContentRef.current.focus();
      }
    }, 100);
  };

  const handleCancelClick = (): void => {
    // Check if cart has items
    const hasItems = wizardState.cart.items.length > 0;
    
    if (hasItems) {
      // Show confirmation dialog
      setShowCancelDialog(true);
    } else {
      // Empty cart - navigate away immediately
      navigate('/');
    }
  };

  const handleCancelConfirm = (): void => {
    // Clear cart and navigate away
    clearCart();
    navigate('/');
  };

  const handleCheckoutComplete = (newOrderId: string): void => {
    setOrderId(newOrderId);
    setWizardState((prev) => ({
      ...prev,
      completedSteps: [...prev.completedSteps, 4],
      currentStep: 5,
    }));
    // Note: Cart clearing happens in ConfirmationPage after Stripe redirect
  };

  if (loading || countdownLoading || eventRegSettingsLoading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
            <p className="mt-4 text-orange-200">Loading registration...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show countdown blocking screen if registration is blocked
  if (registrationBlocked && targetDate) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
            <CardContent className="py-12 px-6 text-center">
              <h1 className="text-4xl font-viking text-white mb-4">
                Registration Opens Soon!
              </h1>
              <p className="text-xl text-orange-200 mb-8">
                Get ready - registration will be available in:
              </p>

              <CountdownTimer targetDate={targetDate} />

              <div className="mt-8 space-y-3">
                <p className="text-white/90">
                  Registration opens on {new Date(targetDate).toLocaleDateString()} at {new Date(targetDate).toLocaleTimeString()}
                </p>
                <p className="text-orange-200/80 text-sm">
                  Check back here to register for tournaments and special events!
                </p>
              </div>

              <div className="mt-8">
                <Link to="/">
                  <Button variant="secondary" size="lg">
                    Return to Home
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // User has prior registrations if they have an event registration OR tournament registrations
  const hasPriorRegistrations = existingRegistration || existingTournamentIds.length > 0;

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      {/* Skip to main content link for keyboard navigation */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-orange-500 focus:text-white focus:rounded-md focus:shadow-lg"
      >
        Skip to main content
      </a>
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Page Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-viking text-white mb-2">
              {hasPriorRegistrations ? 'Add to Registration' : 'Event Registration'}
            </h1>
            <p className="text-sm sm:text-base text-orange-200">
              {hasPriorRegistrations
                ? 'Add tournaments and merchandise to your existing registration'
                : 'Complete your registration for Cactus Cup 2026'}
            </p>
          </div>
          {wizardState.currentStep < 5 && (
            <Button
              variant="outline"
              onClick={handleCancelClick}
              className="border-orange-500 text-orange-200 hover:bg-orange-500/20 w-full sm:w-auto"
              aria-label="Cancel registration and return to home"
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Wizard Stepper */}
      <div className="mb-8">
        <WizardStepper
          steps={hasPriorRegistrations ? WIZARD_STEPS.slice(1) : WIZARD_STEPS}
          currentStep={wizardState.currentStep}
          completedSteps={wizardState.completedSteps}
        />
      </div>

      {/* Progress Message for long operations */}
      {showProgressMessage && (
        <div className="mb-6">
          <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
            <CardContent className="py-6">
              <ProgressMessage 
                message={progressMessage}
                estimatedSeconds={3}
                showTimer={true}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step Content */}
      <main 
        id="main-content" 
        className="mb-8" 
        role="main" 
        aria-label="Registration wizard content"
        ref={mainContentRef}
        tabIndex={-1}
      >
        {wizardState.currentStep === 1 && !hasPriorRegistrations && (
          <ErrorBoundary>
            {/* dayPasses and allDayPasses are two lists on purpose, and they are not
                interchangeable:
                  dayPasses    = what may still be SOLD (gated by dayPassesEnabled, because
                                 a closed product must never be offered).
                  allDayPasses = the whole configured catalogue, used only to work out what
                                 the buyer ALREADY OWNS.
                Gating the second one would make a Full Weekend Pass holder look uncovered
                the moment day-pass sales close, so the wizard would offer them the $30
                spectator pass for the weekend they already bought — and
                create-checkout-session, which reads the ungated day_passes config, would
                then refuse it at checkout. Same data, two different questions. */}
            <EventEntryStep
              onComplete={(data) => handleStepComplete(1, data)}
              fee={eventRegSettings.activeEventRegistrationFee}
              regularFee={eventRegSettings.eventRegistrationFee}
              entryName={eventRegSettings.eventRegistrationName}
              entryDescription={eventRegSettings.eventRegistrationDescription}
              paidEntryEnabled={eventRegSettings.paidEntryEnabled}
              dayPasses={eventRegSettings.dayPassesEnabled ? eventRegSettings.dayPasses : []}
              allDayPasses={eventRegSettings.dayPasses}
              ownedDayPassIds={ownedDayPassIds}
              freeEntryEnabled={eventRegSettings.freeEntryEnabled}
              freeEntryName={eventRegSettings.freeEntryName}
              freeEntryDescription={eventRegSettings.freeEntryDescription}
              freeEntryBenefits={eventRegSettings.freeEntryBenefits}
              paidEntryBenefits={eventRegSettings.paidEntryBenefits}
              spectatorPassEnabled={eventRegSettings.spectatorPassEnabled}
              spectatorPassFee={eventRegSettings.spectatorPassFee}
              spectatorPassName={eventRegSettings.spectatorPassName}
              spectatorPassDescription={eventRegSettings.spectatorPassDescription}
              spectatorPassBenefits={eventRegSettings.spectatorPassBenefits}
              isEarlyBird={eventRegSettings.isEarlyBirdActive}
              earlyBirdEndDate={eventRegSettings.earlyBirdEndDate}
              waiverUrl={eventRegSettings.waiverUrl}
            />
          </ErrorBoundary>
        )}

        {wizardState.currentStep === 2 && (
          <ErrorBoundary>
            <TournamentSelectionStep
              selectedIds={wizardState.tournaments.selectedIds}
              lockedTournamentIds={existingTournamentIds}
              tournaments={tournaments}
              registrationEnabled={eventRegSettings.tournamentRegistrationEnabled}
              waitlistAllowed={!!existingRegistration || ownedDayPassIds.length > 0}
              onComplete={(data) => handleStepComplete(2, data)}
              onBack={hasPriorRegistrations ? () => window.history.back() : handleStepBack}
            />
          </ErrorBoundary>
        )}

        {wizardState.currentStep === 3 && (
          <ErrorBoundary>
            <AddonsSelectionStep
              selections={wizardState.addons.selections}
              existingPurchases={existingAddonPurchases}
              onComplete={(data) => handleStepComplete(3, data)}
              onBack={handleStepBack}
            />
          </ErrorBoundary>
        )}

        {wizardState.currentStep === 4 && (
          <ErrorBoundary>
            <CheckoutStep
              cart={wizardState.cart}
              tournaments={tournaments}
              addons={addons}
              eventRegistrationFee={eventRegSettings.activeEventRegistrationFee}
              eventRegistrationName={eventRegSettings.eventRegistrationName}
              onBack={handleStepBack}
              onComplete={handleCheckoutComplete}
            />
          </ErrorBoundary>
        )}

        {wizardState.currentStep === 5 && orderId && (
          <ErrorBoundary>
            <ConfirmationStep orderId={orderId} isFreeRegistration={isFreeRegistration} />
          </ErrorBoundary>
        )}

        {/* Fallback for step 5 when no order exists (empty cart) */}
        {wizardState.currentStep === 5 && !orderId && (
          <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
            <CardContent className="py-12 text-center">
              <ShoppingCart className="h-12 w-12 text-orange-300 mx-auto mb-3" />
              <p className="text-orange-200 mb-4">Nothing in cart</p>
              <Button
                onClick={() => navigate('/register')}
                variant="outline"
                className="border-orange-500 text-orange-200 hover:bg-orange-500/20"
              >
                Start Registration
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Error display for free registration failures */}
        {freeRegistrationError && (
          <Card className="backdrop-blur-md bg-red-800/90 border-2 border-red-500">
            <CardContent className="py-6">
              <p className="text-white font-semibold">Registration Error</p>
              <p className="text-red-200 mt-2">{freeRegistrationError}</p>
              <Button
                onClick={() => setFreeRegistrationError(null)}
                variant="outline"
                className="mt-4 border-red-400 text-red-200 hover:bg-red-700/50"
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Cancel Confirmation Dialog */}
      <CancelConfirmDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onConfirm={handleCancelConfirm}
      />

      </div>
    </div>
  );
};
